import { Icon, Tooltip } from '@sendible/design-system/src';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleLoadingSkeleton } from './ModuleLoadingSkeleton';
import { ModuleNoData } from './ModuleNoData';
import {
  ModuleShellContainer,
  ModuleHeader,
  ModuleTitleRow,
  ModuleTitle,
  ModuleInfoIcon,
  ModuleBody,
  ModuleMenuButton,
  ModuleFooter,
  HeaderActions,
  ResizeHint,
  DragHandle,
} from './index.styles';
import { useModuleMetrics, useModuleMetricsInfinite } from '../../../../models/reporting';
import { normalisePlatform, MODULE_CODE_TO_TRANSLATION_KEYS } from '../../../../models/reporting/transforms';
import { useReportingContext } from '../../context';
import { MODULE_LABEL_KEYS } from '../../helpers';
import { DynamicModuleBuilder } from '../DynamicModuleBuilder';

interface ModuleShellProps {
  module: ReportModule;
  moduleDefinition?: ModuleDefinition;
  onRemove?: (reportModuleId: string) => void;
  isEditing?: boolean;
}

interface InnerContentProps {
  module: ReportModule;
  compatibleProfileIds: string[];
  selectedProfiles: ReportProfile[];
  supportedPlatforms: PlatformType[];
}

// 1. Sub-component for single-page analytics modules
const RegularModuleContent = ({ module, compatibleProfileIds, selectedProfiles, supportedPlatforms }: InnerContentProps) => {
  const { period } = useReportingContext();
  const { data: metricsResponse, isLoading, isError } = useModuleMetrics(module, compatibleProfileIds, period, true);
  const moduleData = metricsResponse?.data as DynamicModuleData | undefined;

  if (isLoading) return <ModuleLoadingSkeleton />;
  if (isError) return <ModuleNoData reason="error" />;
  if (metricsResponse?.status === 'no_data') {
    return <ModuleNoData reason={metricsResponse.noDataReason} />;
  }

  return (
    <DynamicModuleBuilder
      module={module}
      data={moduleData}
      isLoading={false}
      selectedProfiles={selectedProfiles}
      supportedPlatforms={supportedPlatforms}
    />
  );
};

// 2. Sub-component for paginated post scroll lists
const InfiniteModuleContent = ({ module, compatibleProfileIds, selectedProfiles, supportedPlatforms }: InnerContentProps) => {
  const { period } = useReportingContext();
  const { mergedData, isLoading, isError, fetchNextPage, hasNextPage } = useModuleMetricsInfinite(module, compatibleProfileIds, period, true);

  const metricsResponse = mergedData
    ? ({
        status: (mergedData.data.length > 0 ? 'has_data' : 'no_data') as ModuleStatus,
        noDataReason: mergedData.data.length > 0 ? null : ('empty_result' as NoDataReason),
        data: mergedData as DynamicModuleData,
        metadata: { lastUpdated: new Date().toISOString(), timezone: 'UTC', profilesIncluded: compatibleProfileIds, profilesExcluded: [] },
      } as ModuleMetricsResponse<DynamicModuleData>)
    : undefined;

  const moduleData = metricsResponse?.data as DynamicModuleData | undefined;

  if (isLoading) return <ModuleLoadingSkeleton />;
  if (isError) return <ModuleNoData reason="error" />;
  if (metricsResponse?.status === 'no_data') {
    return <ModuleNoData reason={metricsResponse.noDataReason} />;
  }

  return (
    <DynamicModuleBuilder
      module={module}
      data={moduleData}
      isLoading={false}
      selectedProfiles={selectedProfiles}
      supportedPlatforms={supportedPlatforms}
      onFetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
    />
  );
};

// 3. Orchestration container
export const ModuleShell = memo(({ module, moduleDefinition, onRemove, isEditing = true }: ModuleShellProps) => {
  const { t } = useTranslation('reports');
  const { profiles: selectedProfiles, profileIds } = useReportingContext();

  const supportedPlatforms = (moduleDefinition?.supportedProfileTypes ?? []) as PlatformType[];

  const compatibleProfileIds =
    supportedPlatforms.length > 0
      ? selectedProfiles.filter((p) => supportedPlatforms.some((sp) => normalisePlatform(sp) === normalisePlatform(p.platform))).map((p) => p.id)
      : profileIds;

  const isPostsCardListModule = (module.chartType ?? moduleDefinition?.defaultChartType) === 'PostsCardList';

  const isAutoGeneratedName = module.customName === moduleDefinition?.name;
  const moduleCodeKeys = moduleDefinition?.moduleCode ? MODULE_CODE_TO_TRANSLATION_KEYS[moduleDefinition.moduleCode] : null;

  let title: string;

  if (!isAutoGeneratedName && module.customName) {
    title = module.customName;
  } else if (moduleCodeKeys) {
    title = t(moduleCodeKeys.titleKey);
  } else {
    title = t(MODULE_LABEL_KEYS[module.moduleType]) || module.moduleType;
  }

  const renderContent = () => {
    if (profileIds.length === 0) {
      return (
        <ModuleBody>
          <ModuleNoData reason="no_profiles_selected" />
        </ModuleBody>
      );
    }

    if (supportedPlatforms.length > 0) {
      const selectedNormalised = new Set(selectedProfiles.map((p) => normalisePlatform(p.platform)));
      const hasCompatible = supportedPlatforms.some((platform) => selectedNormalised.has(normalisePlatform(platform)));

      if (!hasCompatible) {
        const platformLabel = normalisePlatform(supportedPlatforms[0]);

        return (
          <ModuleBody>
            <ModuleNoData
              reason="no_compatible_profiles"
              platformLabel={platformLabel}
            />
          </ModuleBody>
        );
      }
    }

    return (
      <ModuleBody>
        {isPostsCardListModule ? (
          <InfiniteModuleContent
            module={module}
            compatibleProfileIds={compatibleProfileIds}
            selectedProfiles={selectedProfiles}
            supportedPlatforms={supportedPlatforms}
          />
        ) : (
          <RegularModuleContent
            module={module}
            compatibleProfileIds={compatibleProfileIds}
            selectedProfiles={selectedProfiles}
            supportedPlatforms={supportedPlatforms}
          />
        )}
      </ModuleBody>
    );
  };

  return (
    <ModuleShellContainer>
      <ModuleHeader $draggable={isEditing}>
        {isEditing && (
          <DragHandle
            className="module-drag-handle"
            aria-hidden
          >
            <Icon
              color="brandDarkTint30"
              name="drag_handle_color"
            />
          </DragHandle>
        )}
        <ModuleTitleRow>
          <ModuleTitle>{title}</ModuleTitle>
          <Tooltip
            content={moduleCodeKeys ? t(moduleCodeKeys.descriptionKey) : ''}
            noDecoration
            defaultPosition="top"
            maxWidth
          >
            <ModuleInfoIcon aria-hidden>
              <Icon
                color="brandDark"
                name="info"
              />
            </ModuleInfoIcon>
          </Tooltip>
        </ModuleTitleRow>
        <HeaderActions>
          {onRemove && (
            <Tooltip
              content={t('new_reports_remove_module')}
              noDecoration
              defaultPosition="top"
            >
              <ModuleMenuButton
                type="button"
                aria-label={t('new_reports_remove_module')}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(module.reportModuleId);
                }}
              >
                <Icon
                  color="brandDarkTint10"
                  name="trash"
                />
              </ModuleMenuButton>
            </Tooltip>
          )}
        </HeaderActions>
      </ModuleHeader>
      {renderContent()}
      {(() => {
        return (
          <ModuleFooter>
            {isEditing && (moduleDefinition?.isResizable ?? true) && (
              <ResizeHint aria-hidden>
                <Icon
                  color="brandDark"
                  name="resize"
                  strokeWidth={1}
                />
              </ResizeHint>
            )}
          </ModuleFooter>
        );
      })()}
    </ModuleShellContainer>
  );
});

ModuleShell.displayName = 'ModuleShell';
